// Design tokens: semantic colors, gradients, shadows and shapes, one value per color scheme under
// the same name (docs/specs/web/design-system.md, section 1). They match the live Created.ai app.
// Components use `var(--cai-...)` or Mantine props, never a raw color: `just web-lint` fails on
// one outside this file. The dark set's type comes from the light set, so a token missing from
// either scheme fails tsc.

type TokenName = `--cai-${string}`;

/** The reference's indigo scale (spec 1.1): Mantine's `indigo` and the primary color. */
export const indigo = [
  "#f5f2ff",
  "#ede9fe",
  "#e2dafc",
  "#d7cff9",
  "#a89df8",
  "#554ef6",
  "#473dd1",
  "#392eac",
  "#2b2181",
  "#20134b",
] as const;

/** Mantine's five shadows, as the live app sets them (spec 1.4). */
export const shadows = {
  xs: "0 1px 2px 0 rgba(0 0 0 / 0.05)",
  sm: "0 1px 3px 0 rgba(0 0 0 / 0.1), 0 1px 2px -1px rgba(0 0 0 / 0.1)",
  md: "0 4px 6px -1px rgba(0 0 0 / 0.1), 0 2px 4px -2px rgba(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgba(0 0 0 / 0.1), 0 4px 6px -4px rgba(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgba(0 0 0 / 0.1), 0 8px 10px -6px rgba(0 0 0 / 0.1)",
};

/** The same in both schemes: sizes and the shapes of large surfaces (spec 1.2). */
const layout = {
  "--cai-header-height": "60px",
  "--cai-sidebar-width": "240px",
  "--cai-sidebar-width-compact": "70px",
  "--cai-page-max-width": "1240px",
  "--cai-panel-width": "795px",
  "--cai-auth-card-width": "398px",
  "--cai-radius-card": "16px",
  "--cai-radius-media": "14px",
  "--cai-radius-panel": "21px",
  "--cai-radius-table": "12px",
  "--cai-radius-pill": "999px",
  // The detail modal's card: up to this wide (and 95vw), with the home cards' 24px corners.
  "--cai-detail-width": "1300px",
  "--cai-radius-modal": "24px",
  "--cai-font-ui": "'Space Grotesk', system-ui, -apple-system, sans-serif",
  "--cai-font-field": "'Inter', system-ui, -apple-system, sans-serif",
} as const satisfies Record<TokenName, string>;

/** The same in both schemes and in every theme family (spec web/themes-engine): veils, swatches. */
export const shared = {
  "--cai-gradient-accent":
    "linear-gradient(133deg, var(--cai-gradient-from), var(--cai-gradient-to))",
  "--cai-gradient-auth-action": "linear-gradient(90deg, #8952c7, #392eac)",
  // A chosen card's ring and check badge (live's `--gradient-selected`): indigo 6 to Mantine's pink
  // 6, in both schemes.
  "--cai-gradient-selected":
    "linear-gradient(133.36deg, var(--mantine-color-indigo-6), var(--mantine-color-pink-6))",
  // Over media: the hover menu pill and the type badge, white on a dark veil (the spec's
  // card-menu; its --cai-color-overlay is the lighter veil, as on the detail's carousel controls).
  "--cai-color-card-menu": "rgba(0, 0, 0, 0.35)",
  "--cai-color-on-overlay": "#ffffff",
  "--cai-color-overlay": "rgba(0, 0, 0, 0.21)",
  // A template card (G14): live's frosted disc and Coming soon pill over the cover (black 40%,
  // blurred), and the scrim under its title.
  "--cai-color-frost": "rgba(0, 0, 0, 0.4)",
  "--cai-gradient-cover-scrim": "linear-gradient(180deg, transparent 61%, rgba(0, 0, 0, 0.7) 100%)",
  // A template's "What you’ll need" rows: live's soft accent.
  "--cai-gradient-soft-accent":
    "linear-gradient(115.21deg, rgba(114, 67, 239, 0.05) 0%, rgba(22, 134, 212, 0.05) 100%)",
  // The detail modal's veil: live stacks its 21% layer on Mantine's 60% black overlay, so white
  // shows as 80/255 (screenshot 10).
  "--cai-color-modal-veil": "rgba(0, 0, 0, 0.68)",
  // The library's full-screen preview (Daniil's lightbox), a viewer's dark in both schemes: the
  // blurred backdrop, its text, the muted facts, and the panels (arrows, strip, details).
  "--cai-color-viewer-backdrop": "rgba(10, 10, 12, 0.88)",
  "--cai-color-viewer-text": "#ffffff",
  "--cai-color-viewer-muted": "rgba(255, 255, 255, 0.65)",
  "--cai-color-viewer-panel": "rgba(255, 255, 255, 0.1)",
  // The editor's transitions (#240), live's in both schemes: the timeline's mark on a cut (a dark
  // rounded square, a white "+" or two triangles, a faint white edge), the Transitions tab's
  // round scroll arrows, and Transition Settings' card where live's preview did not load.
  "--cai-color-track-cut": "rgba(32, 32, 32, 0.8)",
  "--cai-color-track-cut-ink": "#ffffff",
  "--cai-color-track-cut-edge": "rgba(255, 255, 255, 0.15)",
  "--cai-color-scroll-arrow": "rgba(0, 0, 0, 0.75)",
  "--cai-gradient-transition-card":
    "linear-gradient(to bottom right, rgba(79, 70, 229, 0.7), rgba(107, 33, 168, 0.7))",
  "--cai-shadow-form": "0 10px 20px 0 #554ef61a",
  "--cai-shadow-card": "0 1px 3px 0 var(--cai-color-shadow)",
  "--cai-shadow-card-selected":
    "0 0 0 1px #a89df8, 0 10px 15px -3px rgba(85, 78, 246, 0.15), " +
    "0 4px 6px -4px rgba(85, 78, 246, 0.12)",
  "--cai-shadow-product-hover": "0 5px 8px 0 #554ef61a",
  "--cai-shadow-dialog":
    "0 0 32px 4px var(--cai-color-shadow), 0 16px 48px 0 var(--cai-color-shadow)",
  // Live's Skin Tone swatches on the Global Actors filter (`constants/global-filters.ts`).
  "--cai-skin-fair": "#f5d7c4",
  "--cai-skin-light": "#efc3a4",
  "--cai-skin-medium": "#d2996c",
  "--cai-skin-tan": "#b07b4f",
  "--cai-skin-brown": "#8d5524",
  "--cai-skin-dark": "#5d3a1f",
  // Live's eye and hair colors on the AI Actor Builder (`constants/create-avatar.tsx`).
  "--cai-eye-brown": "#88420c",
  "--cai-eye-blue": "#268ac6",
  "--cai-eye-green": "#5a832c",
  "--cai-eye-hazel": "#a27f3b",
  "--cai-eye-gray": "#c8c8c8",
  "--cai-hair-black": "#1d1b19",
  "--cai-hair-brown": "#88420c",
  "--cai-hair-blonde": "#ffffda",
  "--cai-hair-red": "#9b0f02",
  // The free daily case's rarities (spec web/appearance-tab), CS:GO's colours, the same in every
  // theme family and scheme: a theme's label, and the case's cards.
  "--cai-rarity-common": "#4b69ff",
  "--cai-rarity-rare": "#8847ff",
  "--cai-rarity-epic": "#d32ce6",
  "--cai-rarity-legendary": "#eb4b4b",
  "--cai-rarity-mythic": "#e4ae39",
  "--cai-rarity-jackpot": "#ffd84d",
  // The Theme Case the free daily case opens (spec web/case-art): Created.ai's purple, the logo's, the
  // same in every theme family and scheme, as the logo is. Daniil's art carries the case's own colours
  // (spec web/case-art-images); these are its shade, its glow and the light out of it.
  "--cai-case-from": "#6233ff",
  "--cai-case-shade": "#0000002e",
  "--cai-case-glow": "#6233ff59",
  "--cai-case-light": "#ffeca0f2",
  "--cai-case-ray": "#ffdc7873",
  // A generation in progress (ui/GenerationLoader): live's loader's three colours, the same in every
  // theme family and scheme, as live's base palette is.
  "--cai-loader-from": "#ff3399",
  "--cai-loader-mid": "#b033cc",
  "--cai-loader-to": "#6233ff",
  // The case's dark showcase stage, its cards and the brand's pink (spec web/case-polish).
  "--cai-case-pink": "#ff3399",
  "--cai-case-stage-from": "#2c1d6b",
  "--cai-case-stage-via": "#170f38",
  "--cai-case-stage-to": "#0b0820",
  "--cai-case-stage-ray": "#9b4bff1f",
  "--cai-case-stage-shade": "#00000073",
  "--cai-case-card": "#1b1640",
  "--cai-case-card-text": "#e9e6ff",
  "--cai-case-spot": "#c4aaff33",
  "--cai-case-spot-core": "#dcc8ff42",
  "--cai-case-vignette": "#05030fa6",
  // Home 2's "Start here" card (spec ui-perf/pro-studio-redesign): the prototype's dark veil over
  // the accent gradient, and its white button's text, the same in both schemes as the prototype's.
  "--cai-color-start-veil": "rgba(26, 16, 48, 0.65)",
  "--cai-color-start-cta-text": "#16151d",
  ...layout,
} as const satisfies Record<TokenName, string>;

export const lightTokens = {
  "--cai-color-canvas": "#ffffff",
  "--cai-color-page": "#f8f9fa",
  "--cai-color-surface": "#ffffff",
  "--cai-color-surface-hover": "#f8f9fa",
  "--cai-color-skeleton": "#dee2e6",
  "--cai-color-glass": "#fffffff2",
  "--cai-color-primary": "#554ef6",
  "--cai-color-primary-hover": "#473dd1",
  "--cai-color-primary-pressed": "#392eac",
  "--cai-color-primary-soft": "#f5f2ff",
  "--cai-color-primary-soft-hover": "#ede9fe",
  "--cai-color-primary-deep": "#20134b",
  "--cai-color-on-primary": "#ffffff",
  "--cai-color-text": "#495057",
  "--cai-color-text-strong": "#343a40",
  "--cai-color-text-heading": "#000000",
  "--cai-color-text-muted": "#868e96",
  "--cai-color-text-secondary": "#242424",
  "--cai-color-text-primary": "#554ef6",
  "--cai-color-text-link": "#473dd1",
  "--cai-color-text-disabled": "#adb5bd",
  "--cai-color-text-inverse": "#f8f9fa",
  "--cai-color-text-danger": "#fa5252",
  "--cai-color-text-warning": "#fd7e14",
  "--cai-color-text-success": "#12b886",
  "--cai-color-text-info": "#228be6",
  "--cai-color-border": "#dee2e6",
  "--cai-color-border-subtle": "#f1f3f5",
  "--cai-color-border-primary": "#554ef6",
  "--cai-color-border-primary-soft": "#ede9fe",
  "--cai-color-border-active": "#a89df8",
  "--cai-color-icon": "#495057",
  "--cai-color-icon-muted": "#868e96",
  "--cai-color-icon-disabled": "#adb5bd",
  // Live's header theme toggle and composer chevron: primary in light, near white in dark.
  "--cai-color-theme-icon": "#554ef6",
  "--cai-color-interactive-hover": "#f5f2ff",
  "--cai-color-interactive-active": "#ede9fe",
  "--cai-color-interactive-disabled": "#dee2e6",
  // The outlined-light button: its hover and disabled backgrounds differ by scheme.
  "--cai-color-outlined-hover": "#f5f2ff",
  "--cai-color-outlined-disabled": "transparent",
  // A disabled filled button: gray 6 at 10% (light), 15% (dark).
  "--cai-color-filled-disabled": "#868e961a",
  "--cai-color-success": "#12b886",
  "--cai-color-success-soft": "#e7f8f3",
  "--cai-color-warning": "#fab005",
  "--cai-color-warning-soft": "#fef7e6",
  "--cai-color-danger": "#fa5252",
  "--cai-color-danger-soft": "#feeded",
  "--cai-color-info-soft": "#e7f5ff",
  "--cai-color-failed": "#f5f2ff",
  "--cai-color-disabled": "#e9ecef",
  "--cai-color-loading": "#fbfaff",
  "--cai-color-pane": "#f1f3f5",
  // A template card with no cover yet: live's gray-filled (Mantine gray 6, dark gray 8).
  "--cai-color-cover-fallback": "#868e96",
  // A template's category tag: live's gray-light.
  "--cai-color-tag": "#868e961a",
  "--cai-color-auth-card": "#ffffff",
  "--cai-color-auth-field": "#f8f9fa",
  "--cai-color-auth-secondary": "transparent",
  "--cai-color-auth-border": "#f1f3f5",
  "--cai-color-auth-divider": "#f1f3f5",
  "--cai-color-shadow": "#0000001a",
  "--cai-color-ring": "#554ef6",
  "--cai-gradient-from": "#7048e8",
  "--cai-gradient-to": "#e64980",
  "--cai-gradient-page": "linear-gradient(111.62deg, #f5f2ff 0%, #f8f9fa 50%, #e7f5ff 100%)",
  // The video composer's frame slots: a faint violet-to-pink wash.
  "--cai-gradient-slot":
    "linear-gradient(133.36deg, rgba(98, 51, 255, 0.1) 2.7%, rgba(255, 51, 153, 0.1) 100%)",
  // A content card (a brand): a subtle border and a lift on hover in light, neither in dark.
  "--cai-color-card-border": "#f1f3f5",
  "--cai-shadow-card-hover": "0 10px 20px 0 #20134b1a",
  // The video editor's timeline items, as the live editor draws them: a video clip in zinc, a
  // text in sky, a selected item's sky border (E1).
  "--cai-color-track-video": "#e4e4e7",
  "--cai-color-track-video-ink": "#18181b",
  "--cai-color-track-video-border": "#d4d4d8",
  "--cai-color-track-text": "#e0f2fe",
  "--cai-color-track-text-ink": "#0369a1",
  "--cai-color-track-text-border": "#bae6fd",
  // Live's effect bar (#265): green, its name and sparkle darker, a faint edge.
  "--cai-color-track-effect": "#dcfce7",
  "--cai-color-track-effect-ink": "#15803d",
  "--cai-color-track-effect-border": "rgba(0, 0, 0, 0.08)",
  // Live's caption (#239): its timeline bar's fill, label and edge.
  "--cai-color-track-caption": "#ebf8ff",
  "--cai-color-track-caption-ink": "#0284c7",
  "--cai-color-track-caption-border": "rgba(0, 0, 0, 0.08)",
  "--cai-color-track-selected": "rgba(14, 165, 233, 0.85)",
  // A snap's guide on the editor's canvas while a drag holds it (issue 560): live's cyan.
  "--cai-color-canvas-guide": "#48dbfb",
  // A track's band, and the timeline's top rule; the editor's canvas, black in both schemes.
  "--cai-color-track-band": "#f5f5f4",
  "--cai-color-stage": "#000000",
  "--cai-color-track-rule": "#e7e5e4",
  // The achievements' five tiers (spec web/achievements): each tier's name, ring and kicker, a
  // material's colour, the same in every theme family, and readable at 4.5:1 on every family's
  // surface (`CONTRAST_PAIRS`).
  "--cai-tier-extra": "#594fe5",
  "--cai-tier-supporting-role": "#98552a",
  "--cai-tier-leading-role": "#5e6774",
  "--cai-tier-directors-cut": "#836013",
  "--cai-tier-cult-classic": "#c0255b",
  ...shared,
} as const satisfies Record<TokenName, string>;

export const darkTokens: Record<keyof typeof lightTokens, string> = {
  "--cai-color-canvas": "#242424",
  "--cai-color-page": "#1f1f1f",
  "--cai-color-surface": "#2e2e2e",
  "--cai-color-surface-hover": "#3b3b3b",
  "--cai-color-skeleton": "#424242",
  "--cai-color-glass": "#000000d9",
  "--cai-color-primary": "#392eac",
  "--cai-color-primary-hover": "#2b2181",
  "--cai-color-primary-pressed": "#20134b",
  "--cai-color-primary-soft": "#343a40",
  "--cai-color-primary-soft-hover": "#495057",
  "--cai-color-primary-deep": "#f5f2ff",
  "--cai-color-on-primary": "#ffffff",
  "--cai-color-text": "#ffffff",
  "--cai-color-text-strong": "#c9c9c9",
  "--cai-color-text-heading": "#ffffff",
  "--cai-color-text-muted": "#b8b8b8",
  "--cai-color-text-secondary": "#f1f3f5",
  "--cai-color-text-primary": "#a89df8",
  "--cai-color-text-link": "#a89df8",
  "--cai-color-text-disabled": "#696969",
  "--cai-color-text-inverse": "#2e2e2e",
  "--cai-color-text-danger": "#f03e3e",
  "--cai-color-text-warning": "#ff922b",
  "--cai-color-text-success": "#63e6be",
  "--cai-color-text-info": "#4dabf7",
  "--cai-color-border": "#696969",
  "--cai-color-border-subtle": "#424242",
  "--cai-color-border-primary": "#a89df8",
  "--cai-color-border-primary-soft": "#a89df8",
  "--cai-color-border-active": "#a89df8",
  "--cai-color-icon": "#f8f9fa",
  "--cai-color-icon-muted": "#dee2e6",
  "--cai-color-icon-disabled": "#adb5bd",
  "--cai-color-theme-icon": "#f8f9fa",
  "--cai-color-interactive-hover": "#20134b",
  "--cai-color-interactive-active": "#312f48",
  "--cai-color-interactive-disabled": "#3b3b3b",
  "--cai-color-outlined-hover": "#3b3b3b",
  "--cai-color-outlined-disabled": "#3b3b3b",
  "--cai-color-filled-disabled": "#868e9626",
  "--cai-color-success": "#12b886",
  "--cai-color-success-soft": "#0d3315",
  "--cai-color-warning": "#fab005",
  "--cai-color-warning-soft": "#3d2e0a",
  "--cai-color-danger": "#fa5252",
  "--cai-color-danger-soft": "#3d0a0d",
  "--cai-color-info-soft": "#0a2a48",
  "--cai-color-failed": "#20134b",
  "--cai-color-disabled": "#2e2e2e",
  "--cai-color-loading": "#1d2124",
  "--cai-color-pane": "#1d2124",
  "--cai-color-cover-fallback": "#343a40",
  "--cai-color-tag": "#868e9626",
  "--cai-color-auth-card": "#131218",
  "--cai-color-auth-field": "rgba(255, 255, 255, 0.1)",
  "--cai-color-auth-secondary": "rgba(255, 255, 255, 0.1)",
  "--cai-color-auth-border": "transparent",
  "--cai-color-auth-divider": "rgba(255, 255, 255, 0.1)",
  "--cai-color-shadow": "#554ef61a",
  "--cai-color-ring": "#a89df8",
  "--cai-gradient-from": "#7950f2",
  "--cai-gradient-to": "#f06595",
  "--cai-gradient-page":
    "linear-gradient(116.6deg, #1e1c26 0%, #242424 50%, #0a2a48 100%)",
  "--cai-gradient-slot":
    "linear-gradient(133.36deg, rgba(98, 51, 255, 0.15) 2.7%, rgba(255, 51, 153, 0.15) 100%)",
  "--cai-color-card-border": "transparent",
  "--cai-shadow-card-hover": "none",
  "--cai-color-track-video": "#27272a",
  "--cai-color-track-video-ink": "#f4f4f5",
  "--cai-color-track-video-border": "#3f3f46",
  "--cai-color-track-text": "#0369a1",
  "--cai-color-track-text-ink": "#e0f2fe",
  "--cai-color-track-text-border": "#0284c7",
  "--cai-color-track-effect": "#15803d",
  "--cai-color-track-effect-ink": "#dcfce7",
  "--cai-color-track-effect-border": "rgba(255, 255, 255, 0.08)",
  "--cai-color-track-caption": "#1e3a8a",
  "--cai-color-track-caption-ink": "#e0f2fe",
  "--cai-color-track-caption-border": "rgba(255, 255, 255, 0.08)",
  "--cai-color-track-selected": "rgba(56, 189, 248, 0.85)",
  "--cai-color-canvas-guide": "#48dbfb",
  "--cai-color-track-band": "#2b2b2e",
  "--cai-color-stage": "#000000",
  "--cai-color-track-rule": "#3f3f46",
  "--cai-tier-extra": "#a89df8",
  "--cai-tier-supporting-role": "#d8916a",
  "--cai-tier-leading-role": "#c9ced6",
  "--cai-tier-directors-cut": "#c6972f",
  "--cai-tier-cult-classic": "#f1769f",
  ...shared,
};
